import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Trophy, MapPin, Radio, User, MessageCircle, Plus, Medal, Crown, Building2, ShieldCheck } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Logo, Avatar } from "@/components/Brand";
import { useProfile } from "@/lib/api";
const CreateMatchDialog = lazy(() => import("@/components/CreateMatchDialog").then((m) => ({ default: m.CreateMatchDialog })));
import { InstallButton } from "@/components/InstallButton";

const TABS = [
  { to: "/", label: "Accueil", icon: Home, id: "home", end: true },
  { to: "/matchs", label: "Matchs", icon: Trophy, id: "matches" },
  { to: "/terrains", label: "Terrains", icon: MapPin, id: "terrains" },
  { to: "/direct", label: "Direct", icon: Radio, id: "live-pro" },
  { to: "/profil", label: "Profil", icon: User, id: "profile" },
];

const RailLink = ({ tab }) => (
  <NavLink to={tab.to} end={tab.end} data-testid={`rail-tab-${tab.id}`}
    className={({ isActive }) => `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors duration-200 ${isActive ? "bg-[#25e46b]/15 text-[#25e46b]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
    <tab.icon size={19} strokeWidth={2.2} />
    {tab.label}
  </NavLink>
);

const DesktopRail = ({ onCreate }) => {
  const { profile } = useProfile();
  const navigate = useNavigate();
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-white/5 bg-[#080f24] px-5 py-7 lg:flex" data-testid="desktop-rail">
      <Logo className="mb-10 px-2" />
      <p className="mb-3 px-3 text-[10px] font-extrabold uppercase tracking-[0.2em] text-slate-500">Menu</p>
      <nav className="flex flex-col gap-1">
        {TABS.slice(0, 4).map((t) => <RailLink key={t.id} tab={t} />)}
        <RailLink tab={{ to: "/championnats", label: "Championnats", icon: Medal, id: "leagues" }} />
        <RailLink tab={{ to: "/messages", label: "Messages", icon: MessageCircle, id: "messages" }} />
        <RailLink tab={{ to: "/classement", label: "Classement", icon: Crown, id: "leaderboard" }} />
        <RailLink tab={TABS[4]} />
        <RailLink tab={{ to: "/partenaire", label: "Espace partenaire", icon: Building2, id: "partner" }} />
        {profile?.is_admin && <RailLink tab={{ to: "/admin", label: "Administration", icon: ShieldCheck, id: "admin" }} />}
      </nav>
      <button onClick={onCreate} className="fz-btn-primary mt-6 w-full" data-testid="rail-create-match-button">
        <Plus size={18} /> Organiser un match
      </button>
      <div className="mt-auto space-y-4">
        <InstallButton />
        <button onClick={() => navigate("/profil")} className="flex w-full items-center gap-3 rounded-xl border border-white/5 bg-white/[0.03] p-3 text-left transition-colors hover:bg-white/[0.06]" data-testid="rail-user-card">
          <Avatar name={profile?.name} src={profile?.avatar} size={38} />
          <div className="min-w-0">
            <p className="truncate text-sm font-bold">{profile?.name || "Mon profil"}</p>
            <p className="truncate text-xs text-slate-500">{profile?.city || "Île-de-France"} · {profile?.level || "Joueur"}</p>
          </div>
        </button>
      </div>
    </aside>
  );
};

const MobileTopBar = () => (
  <header className="fz-glass sticky top-0 z-30 flex items-center justify-between border-b border-white/5 px-5 py-3 lg:hidden" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
    <Logo size="sm" />
    <NavLink to="/messages" data-testid="mobile-messages-button" className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5">
      <MessageCircle size={19} />
    </NavLink>
  </header>
);

const BottomNav = () => (
  <nav className="fz-glass safe-bottom fixed inset-x-0 bottom-0 z-40 border-t border-white/5 lg:hidden" data-testid="bottom-nav">
    <div className="mx-auto grid max-w-lg grid-cols-5">
      {TABS.map((t) => (
        <NavLink key={t.id} to={t.to} end={t.end} data-testid={`nav-tab-${t.id}`}
          className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold transition-colors duration-200 ${isActive ? "text-[#25e46b]" : "text-slate-500"}`}>
          {({ isActive }) => (
            <>
              <span className={`flex h-8 w-12 items-center justify-center rounded-full transition-colors duration-200 ${isActive ? "bg-[#25e46b]/15" : ""}`}>
                <t.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              </span>
              {t.label}
            </>
          )}
        </NavLink>
      ))}
    </div>
  </nav>
);

export default function AppShell() {
  const [createOpen, setCreateOpen] = useState(false);
  const [createUsed, setCreateUsed] = useState(false);
  const openCreate = () => { setCreateUsed(true); setCreateOpen(true); };
  const { pathname } = useLocation();
  const showFab = ["/", "/matchs", "/terrains"].includes(pathname);
  return (
    <div className="flex min-h-screen bg-[#0b132b]">
      <DesktopRail onCreate={openCreate} />
      <div className="min-w-0 flex-1">
        <MobileTopBar />
        <main className="mx-auto w-full max-w-6xl px-5 pb-32 pt-6 sm:px-8 lg:px-10 lg:pb-16 lg:pt-10">
          <Suspense fallback={<Skeleton className="h-96 rounded-2xl bg-white/5" />}><Outlet context={{ openCreate }} /></Suspense>
        </main>
      </div>
      {showFab && <button onClick={openCreate} data-testid="fab-create-match"
        className="fixed bottom-24 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full fz-gradient text-[#0b132b] shadow-[0_10px_30px_-6px_rgba(37,228,107,0.7)] transition-transform active:scale-95 lg:hidden">
        <Plus size={26} strokeWidth={2.8} />
      </button>}
      <BottomNav />
      {createUsed && <Suspense fallback={null}><CreateMatchDialog open={createOpen} onOpenChange={setCreateOpen} /></Suspense>}
    </div>
  );
}
